// src/app/modules/modulesCle/ModuleCleManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    fetchModulesCleRequest,
    createModuleCleRequest,
    updateModuleCleRequest,
    deleteModuleCleRequest,
} from "../../../../../redux/actions/modulesCleActions";

// ✅ suppose que tu as déjà formations/campagnes Redux
import { fetchFormationsRequest } from "../../../../../redux/actions/formationsActions";
import { fetchCampagnesRequest } from "../../../../../redux/actions/campagnesActions";

import "../../RessourcesStyles/ModuleCleManagement.css";

const emptyForm = {
    nom: "",
    reference: "",
    description: "",
    active: true,
    formationId: "",
    campagneId: "",
};

const norm = (v) => String(v || "").trim().toLowerCase();

export default function ModuleCleManagement() {
    const dispatch = useDispatch();

    const modulesState = useSelector((s) => s.modulesCle || {});
    const formationsState = useSelector((s) => s.formations || {});
    const campagnesState = useSelector((s) => s.campagnes || {});

    const modules = modulesState.modulesCle || [];
    const formations = formationsState.formations || formationsState.list || [];
    const campagnes = campagnesState.campagnes || campagnesState.list || campagnesState.visibleList || [];

    const loading = !!(modulesState.loading || formationsState.loading || campagnesState.loading);

    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        dispatch(fetchModulesCleRequest());
        // si ces modules existent chez toi, sinon tu enlèves
        dispatch(fetchFormationsRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    const filtered = useMemo(() => {
        const s = norm(q);
        return (modules || []).filter((m) => {
            const ok =
                !s ||
                norm(m?.nom).includes(s) ||
                norm(m?.reference).includes(s) ||
                norm(m?.formationNom).includes(s) ||
                norm(m?.campagneNom).includes(s);
            return ok;
        });
    }, [modules, q]);

    const openAdd = () => {
        setOpen(true);
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
    };

    const openEdit = (m) => {
        setOpen(true);
        setIsEdit(true);
        setCurrentId(m.id);
        setForm({
            nom: m.nom || "",
            reference: m.reference || "",
            description: m.description || "",
            active: m.active !== false,
            formationId: String(m.formationId || ""),
            campagneId: String(m.campagneId || ""),
        });
    };

    const close = () => {
        setOpen(false);
        setForm(emptyForm);
    };

    const validate = () => {
        if (!form.nom.trim()) return "Nom obligatoire";
        if (!form.formationId) return "Formation obligatoire";
        if (!form.campagneId) return "Campagne obligatoire";
        return null;
    };

    const submit = () => {
        const err = validate();
        if (err) {
            Swal.fire({ icon: "warning", text: err, timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nom: form.nom.trim(),
            reference: form.reference?.trim() || null,
            description: form.description?.trim() || null,
            active: !!form.active,
            formationId: Number(form.formationId),
            campagneId: Number(form.campagneId),
        };

        if (isEdit && currentId) dispatch(updateModuleCleRequest(currentId, payload));
        else dispatch(createModuleCleRequest(payload));

        close();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irréversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
        }).then((r) => {
            if (r.isConfirmed) dispatch(deleteModuleCleRequest(id));
        });
    };

    return (
        <div className="mcPage">
            <div className="mcHeader">
                <div>
                    <h2>Modules</h2>
                </div>
                <button className="mcBtn mcBtnPrimary" onClick={openAdd}>
                    + Ajouter module
                </button>
            </div>

            <div className="mcTools">
                <input
                    className="mcSearch"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Rechercher (nom / ref / formation / campagne)"
                />
            </div>

            <div className="mcCard">
                <div className="mcTableWrap">
                    <table className="mcTable">
                        <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Référence</th>
                            <th>Formation</th>
                            <th>Campagne</th>
                            <th>Actif</th>
                            <th style={{ width: 180 }}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ textAlign: "center", padding: 18, opacity: 0.7 }}>
                                    Aucun module
                                </td>
                            </tr>
                        ) : (
                            filtered.map((m) => (
                                <tr key={m.id}>
                                    <td>{m.nom}</td>
                                    <td>{m.reference || "-"}</td>
                                    <td>{m.formationNom || "-"}</td>
                                    <td>{m.campagneNom || "-"}</td>
                                    <td>
                                        <span className={`mcPill ${m.active ? "ok" : "ko"}`}>{m.active ? "Oui" : "Non"}</span>
                                    </td>
                                    <td>
                                        <div className="mcActions">
                                            <button className="mcBtn mcBtnLight" onClick={() => openEdit(m)}>
                                                Modifier
                                            </button>
                                            <button className="mcBtn mcBtnDanger" onClick={() => onDelete(m.id)}>
                                                Supprimer
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>

                    {loading && <div className="mcLoading">Chargement...</div>}
                </div>
            </div>

            {open && (
                <div className="mcModal" role="dialog" aria-modal="true" onMouseDown={close}>
                    <div className="mcModalPanel" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="mcModalHead">
                            <h3>{isEdit ? "Modifier Module" : "Ajouter Module"}</h3>
                            <button className="mcIconBtn" onClick={close}>
                                ✕
                            </button>
                        </div>

                        <div className="mcGrid">
                            <div className="mcField">
                                <label>Nom *</label>
                                <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                            </div>

                            <div className="mcField">
                                <label>Référence</label>
                                <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                            </div>

                            <div className="mcField">
                                <label>Formation *</label>
                                <select value={form.formationId} onChange={(e) => setForm({ ...form, formationId: e.target.value })}>
                                    <option value="">— Choisir —</option>
                                    {formations.map((f) => (
                                        <option key={f.id} value={String(f.id)}>
                                            {f.nom || f.name || `Formation #${f.id}`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mcField">
                                <label>Campagne *</label>
                                <select value={form.campagneId} onChange={(e) => setForm({ ...form, campagneId: e.target.value })}>
                                    <option value="">— Choisir —</option>
                                    {campagnes.map((c) => (
                                        <option key={c.id} value={String(c.id)}>
                                            {c.nom || c.name || `Campagne #${c.id}`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mcField mcFull">
                                <label>Description</label>
                                <textarea
                                    rows={4}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                />
                            </div>

                            <div className="mcField">
                                <label>Actif</label>
                                <select value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
                                    <option value="1">Oui</option>
                                    <option value="0">Non</option>
                                </select>
                            </div>
                        </div>

                        <div className="mcModalFoot">
                            <button className="mcBtn mcBtnLight" onClick={close}>
                                Annuler
                            </button>
                            <button className="mcBtn mcBtnPrimary" onClick={submit}>
                                {isEdit ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
